/**
 * Token storage — the one place the web deliberately does NOT copy the app.
 *
 * The app persists both tokens in redux-persist. A browser is a different
 * threat model and the refresh token NEVER EXPIRES (killed only by logout or
 * a secret change), which makes it the highest-value secret in the product.
 *
 * Interim scheme, per HexagieApp docs/web-auth-flow-implementation.md §6.2:
 *   - authToken  -> memory, mirrored to sessionStorage so a reload inside the
 *                   tab survives. Dies with the tab.
 *   - refreshToken -> localStorage, ONLY because "keep me signed in" is
 *                   currently assumed. Gated behind KEEP_SIGNED_IN so it can
 *                   be switched off in one line.
 *
 * Open decision (D1): the recommended end state is an HttpOnly, Secure,
 * SameSite=Lax cookie set by the backend, which no JS can read. When that
 * lands, only this file changes.
 *
 * Never put a token in a URL, a query param, or a non-HttpOnly cookie.
 */

const AUTH_KEY = "sayagie.auth.token";
const REFRESH_KEY = "sayagie.auth.refresh";

/** Flip to false to make the whole session die with the tab (see D3). */
const KEEP_SIGNED_IN = true;

let authToken = null;
const listeners = new Set();

/** Storage can throw in private mode or with site data blocked. */
const safeRead = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const safeWrite = (storage, key, value) => {
  try {
    if (value == null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    /* storage unavailable — memory still holds the token for this page */
  }
};

const notify = () => listeners.forEach((listener) => listener(getAuthToken()));

export const getAuthToken = () => {
  if (authToken) return authToken;

  authToken = safeRead(sessionStorage, AUTH_KEY);
  return authToken;
};

export const getRefreshToken = () =>
  KEEP_SIGNED_IN ? safeRead(localStorage, REFRESH_KEY) : null;

export const setTokens = ({ authToken: nextAuth, refreshToken }) => {
  if (nextAuth) {
    authToken = nextAuth;
    safeWrite(sessionStorage, AUTH_KEY, nextAuth);
  }

  if (refreshToken && KEEP_SIGNED_IN) {
    safeWrite(localStorage, REFRESH_KEY, refreshToken);
  }

  notify();
};

export const clearTokens = () => {
  authToken = null;
  safeWrite(sessionStorage, AUTH_KEY, null);
  safeWrite(localStorage, REFRESH_KEY, null);
  notify();
};

export const isAuthenticated = () => Boolean(getAuthToken());

/**
 * Subscribe to token changes, including those made in another tab (W3).
 * A logout in one tab must not leave another tab holding a live UI.
 *
 * @param {(token: string|null) => void} listener
 * @returns {() => void} unsubscribe
 */
export const onTokenChange = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key !== REFRESH_KEY) return;

    // The refresh token was cleared elsewhere: that tab logged out.
    if (event.newValue == null) {
      authToken = null;
      safeWrite(sessionStorage, AUTH_KEY, null);
      notify();
    }
  });
}
